import { type VariantProps, cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

import { cn } from "@/utils/ui";

const buttonVariants = cva(
  [
    // base
    "inline-flex items-center justify-center gap-2 whitespace-nowrap transition-[color] ring-gray-border-subtle/20 outline-gray-border-subtle/40",
    // focus
    "focus-visible:ring-4 focus-visible:outline-1 aria-invalid:focus-visible:ring-0",
    // disabled
    "disabled:pointer-events-none disabled:opacity-50",
    // icon
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
  ],
  {
    variants: {
      intent: {
        link: "bg-transparent text-flavor-fg-primary underline-offset-4 hover:underline !h-auto",
        soft: "bg-flavor-ui hover:bg-flavor-ui-hover text-flavor-fg-secondary",
        solid:
          "bg-flavor-solid hover:bg-flavor-solid-hover text-gray-fg-primary",
        ghost: "border-transparent text-gray-fg-primary hover:bg-flavor-ui",

        outline:
          "bg-flavor-ui/20 hover:bg-flavor-ui-hover/30 border border-flavor-border hover:border-flavor-border-hover text-flavor-fg-secondary",
        plain:
          "bg-transparent hover:bg-transparent border-transparent hover:border-transparent text-flavor-fg-primary hover:brightness-110",
      },
      size: {
        xs: "h-6 px-2 text-xs [&_svg:not([class*='size-'])]:size-3 has-[svg]:pl-2",
        sm: "h-8 px-4 text-sm [&_svg:not([class*='size-'])]:size-4 has-[svg]:pl-3",
        md: "h-9 px-5 text-sm [&_svg:not([class*='size-'])]:size-4 has-[svg]:pl-4",
        lg: "h-10 px-6 py-2 [&_svg:not([class*='size-'])]:size-5 has-[svg]:pl-5",
        xl: "h-12 px-8 text-lg [&_svg:not([class*='size-'])]:size-6 has-[svg]:pl-7",
        icon: "size-10 [&_svg:not([class*='size-'])]:size-5",
        iconSm: "size-8 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      intent: "solid",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  intent,
  flavor = "accent",
  alpha = false,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    flavor?: "accent" | "gray" | "destructive";
    alpha?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      data-flavor={`${flavor}${alpha ? "a" : ""}`}
      className={cn(
        buttonVariants({
          intent,
          size,
          className,
        }),
      )}
      {...props}
    />
  );
}

export { Button, buttonVariants };
